import { calculateMonthDifference, dateFormatServer } from '@alluradirect/react';
import { UnavailableDates } from 'components/views/pages/Checkout/hooks/usePropertyUnavilableDates';
import dayjs from 'dayjs';
import { usePrevious } from 'hooks/usePrevious';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateUtils, DayModifiers, Modifiers } from 'react-day-picker';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { toast } from 'react-toastify';

/** Sources:
 * https://react-day-picker-v7.netlify.app/examples/selected-range-enter
 * https://react-day-picker-v7.netlify.app/examples/selected-range
 *
 */

const NUM_MONTHS = 2;

export interface SelectedDateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface Args {
  initialStart?: string;
  initialEnd?: string;
  minNightsDates: UnavailableDates['MIN_NIGHTS_DATES'];
  lastDateOfRates: string;
  lastBookableDate: string;
  unavailabilityArrival: string[];
  unavailabilityDeparture: string[];
  cannotArrive: string[];
  cannotDepart: string[];
}

const isSelectingFirstDay = (from: Date | undefined, to: Date | undefined, day: Date) => {
  const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
  const isRangeSelected = from && to;
  return !from || isBeforeFirstDay || isRangeSelected;
};

export const useDateRangePicker = ({
  initialStart,
  initialEnd,
  minNightsDates,
  lastDateOfRates,
  lastBookableDate,
  unavailabilityArrival,
  unavailabilityDeparture,
  cannotArrive,
  cannotDepart,
}: Args) => {
  const initialDate = initialStart ? dayjs(initialStart).toDate() : new Date();
  const [currYear, setCurrYear] = useState(initialDate.getFullYear());
  const [currMonth, setCurrMonth] = useState(initialDate.getMonth() + 1);

  // Keep track of the last day for mouseEnter.
  const [enteredTo, setEnteredTo] = useState<Date | undefined>(undefined);

  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>({
    from: initialStart ? dayjs(initialStart).toDate() : undefined,
    to: initialEnd ? dayjs(initialEnd).toDate() : undefined,
  });

  const isSelectingStart = useMemo(() => !selectedDateRange.from, [selectedDateRange.from]);

  const isSelectingEnd = useMemo(() => selectedDateRange.from && !selectedDateRange.to, [
    selectedDateRange.from,
    selectedDateRange.to,
  ]);

  const { isXs } = useScreenWidth();

  const initialMonths = useMemo(() => {
    if (!initialStart || !initialEnd) return NUM_MONTHS;

    const monthDiff = Math.abs(calculateMonthDifference(dayjs(initialStart), dayjs(initialEnd))) + 1;
    if (monthDiff > NUM_MONTHS) {
      return monthDiff;
    }
    return NUM_MONTHS;
  }, [initialEnd, initialStart]);

  const [numMonths, setNumMonths] = useState(initialMonths);
  const prevIsXs = usePrevious(isXs);

  useEffect(() => {
    if (prevIsXs && prevIsXs && !isXs) {
      setNumMonths(NUM_MONTHS);
    }
  }, [isXs, prevIsXs]);

  const resetState = () => {
    setSelectedDateRange({ from: undefined, to: undefined });
    setEnteredTo(undefined);
  };

  const handleDayClick = (day: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled || modifiers.minNight) {
      return;
    }
    const { from, to } = selectedDateRange;

    if (from && to && day >= from && day <= to) {
      resetState();
      return;
    }
    const isSelectingStart = isSelectingFirstDay(from, to, day);
    if (isSelectingStart) {
      if (dayjs(day).startOf('day').unix() > dayjs(lastBookableDate).startOf('day').unix()) {
        toast.error(`Last bookable arrival date is ${lastBookableDate}`, { autoClose: 10000 });
        return;
      }
      // Selecting first day in range
      const day_string = day.getFullYear() + '-' + ('0' + (day.getMonth() + 1)).slice(-2) + '-' + ('0' + day.getDate()).slice(-2) ;
      const cantArrive = cannotArrive.some(date => date === day_string);
      if( !cantArrive ){
        // Check if there's a min nights conflict with upcoming unbookable date
        let minNightDays = 1;
        for (let i = 0; i < minNightsDates.length; i++) {
          const currMinNightDate = minNightsDates[i];
          if (Object.keys(currMinNightDate)[0] === day_string) {
            minNightDays = Object.values(currMinNightDate)[0];
            break;
          }
        }

        if (minNightDays > 1) {
          const dayUnix = dayjs(day).unix();
          const nextUnavailableDeparture = unavailabilityDeparture
            .map(d => dayjs(d).unix())
            .filter(d => d > dayUnix)
            .sort((a, b) => a - b)[0];

          if (nextUnavailableDeparture && nextUnavailableDeparture <= dayjs(day).add(minNightDays, 'day').unix()) {
            toast.error(`Minimum ${minNightDays} nights requirement cannot be met due to unbookable dates`);
            return;
          }
        }

        setSelectedDateRange({ from: day, to: undefined });
        setEnteredTo(undefined);
      } else {
        toast.error('Cannot arrive on ' + day_string);
      }
    } else {
      // Selecting last day in range

      const day_string = day.getFullYear() + '-' + ('0' + (day.getMonth() + 1)).slice(-2) + '-' + ('0' + day.getDate()).slice(-2) ;
      const cantDepart = cannotDepart.some(date => date === day_string);
      if( !cantDepart ){
      setSelectedDateRange(curr => ({ from: curr.from, to: day }));
      setEnteredTo(day);
      } else {
        toast.error('Cannot depart on ' + day_string);
      }

    }
  };

  const handleDayMouseEnter = (day: Date, modifiers: DayModifiers) => {
    if (modifiers.minNight) {
      return;
    }
    const { from, to } = selectedDateRange;
    if (!isSelectingFirstDay(from, to, day)) {
      setEnteredTo(day);
    }
  };

  const calculateMinNightsDates = useCallback(() => {
    const minNights: Date[] = [];

    if (!selectedDateRange.from) return minNights;

    for (let i = 0; i < minNightsDates.length; i++) {
      const currMinNightDate = minNightsDates[i];
      const date = Object.keys(currMinNightDate)[0];
      const days = Object.values(currMinNightDate)[0];

      if (date === dateFormatServer(selectedDateRange.from)) {
        for (let j = 1; j < days; j++) {
          const disabledMinNightDate = dayjs(selectedDateRange.from).add(j, 'day').toDate();
          minNights.push(dayjs(disabledMinNightDate).toDate());
        }
      }
    }

    return minNights;
  }, [minNightsDates, selectedDateRange.from]);

  const modifiers = useMemo(() => {
    // Min nights dates are not selectable but appear as a special form of date with modifier --minNight css class
    const minNightMods = calculateMinNightsDates();

    const modifiers: Partial<Modifiers> = {
      start: selectedDateRange.from,
      end: selectedDateRange.to ?? enteredTo,
      minNight: minNightMods,
    };
    return modifiers;
  }, [calculateMinNightsDates, enteredTo, selectedDateRange.from, selectedDateRange.to]);

  const selectedDays = [
    selectedDateRange.from,
    { from: selectedDateRange.from, to: selectedDateRange.to ?? enteredTo },
  ];

  const disabled = useMemo(() => {
    const dates = [];
    let before = new Date();
    // Allow dates up to lastDateOfRates to be visually enabled so their rates show.
    // Selection of a start date past lastBookableDate is blocked in handleDayClick.
    let after = dayjs(lastDateOfRates).add(1, 'day').toDate();

    const lastValidCheckoutUnix = dayjs(lastDateOfRates).add(1, 'day').unix();
    const validUnavailabilityDeparture = unavailabilityDeparture.filter(
      date => dayjs(date).unix() !== lastValidCheckoutUnix
    );

    // It should not be possible to select the same from and to date
    if (selectedDateRange.from && !selectedDateRange.to) {
      dates.push(selectedDateRange.from);
    }

    // Disable dates according to picking start date, end date or selected both start and end date
    if (!isSelectingStart && !isSelectingEnd) {
      dates.push(
        ...unavailabilityArrival
          .filter(date => {
            return date !== dateFormatServer(selectedDateRange.to as Date);
          })
          .map(date => dayjs(date).toDate()),
      );
      dates.push(
        ...validUnavailabilityDeparture
          .filter(date => {
            return date !== dateFormatServer(selectedDateRange.from as Date);
          })
          .map(date => dayjs(date).toDate()),
      );
    }
    if (isSelectingStart) {
      dates.push(...unavailabilityArrival.map(date => dayjs(date).toDate()));
    }
    if (isSelectingEnd) {
      before = selectedDateRange.from as Date;
      after = dayjs(lastDateOfRates).add(1, 'day').toDate();
      dates.push(...validUnavailabilityDeparture.map(date => dayjs(date).toDate()));

      // When selecting end min-nights dates kick-in

      // When selecting end also disable any dates past the next disabled date
      const fromDate = selectedDateRange.from;
      for (let i = 0; i < dates.length; i++) {
        if (dayjs(dates[i]).unix() > dayjs(fromDate).unix()) {
          after = dates[i];
          break;
        }
      }
    }

    // this hides all prices and makes dates unselectable after choosing to date
    // if (selectedDateRange.to && selectedDateRange.from) {
    //   after = dayjs(selectedDateRange.to).toDate();
    // }

    // Ensure everything after the last date of rates is always disabled
    if (dayjs(after).add(1, 'day').unix() > dayjs(lastDateOfRates).add(1, 'day').unix()) {
      after = dayjs(lastDateOfRates).add(1, 'day').toDate();
    }

    return [...dates, { before }, { after }];
  }, [
    isSelectingEnd,
    isSelectingStart,
    lastDateOfRates,
    selectedDateRange.from,
    selectedDateRange.to,
    unavailabilityArrival,
    unavailabilityDeparture,
  ]);

  const month = useMemo(() => {
    return dayjs()
      .year(currYear)
      .month(currMonth - 1)
      .toDate();
  }, [currMonth, currYear]);

  return {
    month,
    initialDate,
    handleDayClick,
    selectedDateRange,
    modifiers,
    selectedDays,
    handleDayMouseEnter,
    disabledDays: disabled,
    resetState,
    currMonth,
    currYear,
    setCurrMonth,
    setCurrYear,
    numMonths,
    setNumMonths,
  };
};
